import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Moon, Sun, LogOut } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useKeycloakContext } from '@/contexts/KeycloakContext';

const Navbar = () => {
  const [isDarkMode, setIsDarkMode] = React.useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, signOut, isAuthenticated, isLoading } = useKeycloakContext();
  
  console.log('Navbar - Auth State:', { user, isAuthenticated, isLoading });
  
  const toggleDarkMode = () => {
    setIsDarkMode(!isDarkMode);
    document.documentElement.classList.toggle('dark');
  };

  const isActivePath = (path: string) => {
    return location.pathname === path;
  };

  const handleFreeAIClick = () => {
    navigate('/free-ai');
  };

  const handleSignOut = async () => {
    try {
      await signOut();
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  return (
    <nav className="border-b border-gray-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          <div className="flex items-center">
            <Link to="/" className="flex items-center">
              <div className="w-8 h-8 rounded-md bg-primary flex items-center justify-center mr-2">
                <span className="text-primary-foreground font-semibold">N</span>
              </div>
              <span className="font-bold text-lg">NISOO</span>
            </Link>
          </div>

          <div className="hidden md:flex space-x-8">
            <Link 
              to="/products" 
              className={cn(
                "nav-link",
                isActivePath('/products') && "text-primary font-semibold"
              )}
            >
              Products
            </Link>
            <Link 
              to="/blog" 
              className={cn(
                "nav-link",
                isActivePath('/blog') && "text-primary font-semibold"
              )}
            >
              Blog
            </Link>
            <button
              onClick={handleFreeAIClick}
              className={cn(
                "nav-link",
                (isActivePath('/free-ai')) && "text-primary font-semibold",
                "hover:text-primary free-ai-button"
              )}
            >
              Free-AI
            </button>
            <Link 
              to="/about" 
              className={cn(
                "nav-link",
                isActivePath('/about') && "text-primary font-semibold"
              )}
            >
              About
            </Link>
            <Link 
              to="/contact" 
              className={cn(
                "nav-link",
                isActivePath('/contact') && "text-primary font-semibold"
              )}
            >
              Contact
            </Link>
          </div>

          <div className="flex items-center space-x-4">
            <button 
              onClick={toggleDarkMode}
              className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
              aria-label="Toggle dark mode"
            >
              {isDarkMode ? <Sun size={20} /> : <Moon size={20} />}
            </button>
            
            {isLoading ? (
              <div className="w-20 h-8 bg-gray-200 animate-pulse rounded"></div>
            ) : isAuthenticated ? (
              <div className="flex items-center space-x-2">
                {user?.picture && (
                  <img 
                    src={user.picture} 
                    alt={user.name || 'Avatar'} 
                    className="w-8 h-8 rounded-full" 
                  />
                )}
                <Button variant="outline" size="sm" onClick={() => navigate('/profile')}>
                  Profile
                </Button>
                <Button 
                  variant="destructive" 
                  size="sm" 
                  onClick={handleSignOut}
                >
                  <LogOut className="mr-2 h-4 w-4" /> Logout
                </Button>
              </div>
            ) : (
              <>
                <Button variant="outline" size="sm" onClick={() => navigate('/auth/login')}>
                  Sign In
                </Button>
                <Button size="sm" onClick={() => navigate('/auth/register')}>
                  Sign Up
                </Button>
              </>
            )}
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
